import React, { createContext, useState, useContext, ReactNode } from 'react'

type HistoryContextType = {
  recordChange: (newState: unknown) => void
  undo: () => unknown | null
  redo: () => unknown | null
  clearHistory: () => void
  canUndo: () => boolean
  canRedo: () => boolean
}

const defaultValue: HistoryContextType = {
  recordChange: () => null,
  undo: () => null,
  redo: () => null,
  clearHistory: () => null,
  canUndo: () => false,
  canRedo: () => false,
}

const HistoryContext = createContext<HistoryContextType>(defaultValue)

interface HistoryProps {
  children: ReactNode
}

export const HistoryProvider: React.FC<HistoryProps> = ({ children }) => {
  const [history, setHistory] = useState<unknown[]>([''])
  const [historyIndex, setHistoryIndex] = useState(-1) // -1 As the initial new state is not to be reverted

  const recordChange = (newState: unknown) => {
    // Do not save history if the state is empty and no history has yet been saved, or no change occured
    if (
      (!newState && historyIndex == -1) ||
      newState === history[historyIndex]
    ) {
      return
    }
    const newHistory = history.slice(0, historyIndex + 1)
    newHistory.push(newState)
    setHistory(newHistory)
    setHistoryIndex(newHistory.length - 1)
  }

  const undo = (): unknown => {
    if (canUndo()) {
      setHistoryIndex(historyIndex - 1)
      return history[historyIndex - 1]
    }
    return null
  }

  const redo = (): unknown => {
    if (canRedo()) {
      setHistoryIndex(historyIndex + 1)
      return history[historyIndex + 1]
    }
    return null
  }

  const clearHistory = () => {
    setHistory([])
    setHistoryIndex(-1)
  }

  const canUndo = () => historyIndex > 0

  const canRedo = () => historyIndex < history.length - 1

  return (
    <HistoryContext.Provider
      value={{ recordChange, undo, redo, clearHistory, canUndo, canRedo }}
    >
      {children}
    </HistoryContext.Provider>
  )
}

export const useHistory = () => useContext(HistoryContext)
